import React, { useCallback } from 'react'
import { ConfirmationDialog, useModalContext } from '@sketch/components'
import { useToast } from '@sketch/toasts'
import { useFlag } from '@sketch/modules-common'
import { useMarkAllCommentsAsReadMutation } from '@sketch/gql-types'

interface MarkAllCommentsAsReadConfirmationModalProps {
  shareIdentifier: string
  frameUuid?: string
  pageUuid?: string
  onConfirm?: () => void
}

interface MarkAllCommentsAsReadInput {
  shareIdentifier: string
  frameUuid?: string
  pageUuid?: string
}

const useMarkAllCommentsAsRead = () => {
  const includeHasUnreadComments = useFlag('pages-unread-annotations')
  const [mutation, { loading }] = useMarkAllCommentsAsReadMutation({
    onError: 'show-toast',
  })

  const markAllCommentsAsRead = useCallback(
    (variables: MarkAllCommentsAsReadInput) =>
      mutation({
        variables: {
          ...variables,
          includeHasUnreadComments,
        },
      }),
    [mutation, includeHasUnreadComments]
  )

  return { markAllCommentsAsRead, loading }
}

export const useMarkAllCommentsAsReadConfirmationModal = () => {
  const { showModal, hideModal } = useModalContext()
  const { markAllCommentsAsRead, loading: isMarkingAllAsRead } =
    useMarkAllCommentsAsRead()
  const { showToast } = useToast()

  const showConfirmationModal = useCallback(
    (props: MarkAllCommentsAsReadConfirmationModalProps) => {
      const { shareIdentifier, frameUuid, pageUuid, onConfirm } = props

      const isFrameContext = !!frameUuid
      const messageText = isFrameContext
        ? 'All comments in this frame will be marked as read.'
        : 'All comments in this page will be marked as read.'

      showModal(ConfirmationDialog, {
        title: 'Mark all comments as read?',
        children: <p>{messageText}</p>,
        confirmButton: {
          text: 'Mark as Read',
          variant: 'primary',
        },
        cancelButton: {
          text: 'Cancel',
        },
        onConfirm: () => {
          hideModal()
          markAllCommentsAsRead({
            shareIdentifier,
            frameUuid,
            pageUuid,
          })
          showToast('All comments have been marked as read', 'positive')
          onConfirm?.()
        },
        onHide: () => {
          hideModal()
        },
      })
    },
    [showModal, hideModal, markAllCommentsAsRead, showToast]
  )

  return {
    showConfirmationModal,
    isMarkingAllAsRead,
  }
}
